/**
 * NESTED_TABLE tiles: backend response → `<app-nested-table>` data.
 *
 * The backend computes a `levels`-shaped component itself (kaveri-lite
 * NestedTableExecutionService): it returns the level-0 rows with each level's
 * rows nested under that level's `childrenField`, keyed by field. So the tile
 * only adds what the table needs to draw them — the levels, titles and labels
 * from the payload — and the tile Format's look.
 */

import { normalizeTableCellConfig, type TableCellConfig } from '../../../chart-components/unified-table-matrix-chart/table-cell-config';
import type { NestedTableAction, NestedTableColumn, NestedTableData, NestedTableLevel, NestedTableLevelQuery, NestedTableRow, NestedTableRowSave } from '../nested-table/nested-table.types';
import { NESTED_THEME_KEYS } from '../nested-table/nested-table.types';
import { normalizeNestedQueryRows } from '../nested-table/nested-table-query.utils';

/** Keys a payload column shares with the renderer's column, copied as they are. */
interface PayloadColumnBase {
  field?: string;
  label?: string;
  expander?: boolean;
  /** input / dropdown: must be filled before the row's Save shows. */
  required?: boolean;
  cellConfig?: TableCellConfig;
  /** Paired column: a second row key shown in the same cell ("6% - 10%"). */
  secondField?: string;
  separator?: string;
  /** Fetched for the rows but not shown on its own (a paired column's second key). */
  hidden?: boolean;
  /** Round off: decimal places numeric values are shown with. */
  decimals?: number;
  /** Number scale: auto / K / M / B. */
  scale?: string;
}

interface PayloadLevel {
  title?: string;
  childrenField?: string;
  columns?: PayloadColumnBase[];
  /** Input / dropdown columns the table adds itself (not in the dataset). */
  inputColumns?: Array<PayloadColumnBase & { type?: string; options?: string[] }>;
  /** A Save button on edited rows (see NestedTableRowSave). */
  rowSave?: NestedTableRowSave;
  /** Display order of every column, editable ones included. */
  columnOrder?: string[];
  /** Buttons above this level's table (Export, navigate, API…). */
  actions?: NestedTableAction[];
}

/** The rows inside a compute / generate response (`data`, `result.data`, or the array itself). */
export function nestedTableRows(response: unknown): NestedTableRow[] {
  if (Array.isArray(response)) return response as NestedTableRow[];
  const record = (response ?? {}) as Record<string, unknown>;
  const inner = (record['result'] ?? record) as Record<string, unknown>;
  const data = inner?.['data'] ?? record['data'];
  return Array.isArray(data) ? (data as NestedTableRow[]) : [];
}

/** `<app-nested-table>` data for a tile from its payload and the backend's rows. */
export function nestedTableData(
  rows: NestedTableRow[],
  payload: Record<string, unknown> | undefined,
  title = '',
  format: Record<string, unknown> = {},
): NestedTableData {
  const levels = (Array.isArray(payload?.['levels']) ? payload!['levels'] : []) as PayloadLevel[];
  const levelQueries = (Array.isArray(payload?.['levelQueries']) ? payload!['levelQueries'] : []) as NestedTableLevelQuery[];
  const subtitle = typeof payload?.['subtitle'] === 'string' ? payload['subtitle'] as string : '';
  return {
    title,
    ...(subtitle ? { subtitle } : {}),
    levels: levels.map(toLevel),
    staticData: levelQueries[0] ? normalizeNestedQueryRows(rows, levelQueries[0].columns) : rows,
    ...(levelQueries.length ? { levelQueries } : {}),
    ...(typeof payload?.['levelApiUrl'] === 'string' && payload['levelApiUrl'] ? { levelApiUrl: payload['levelApiUrl'] as string } : {}),
    ...themeFromFormat(format),
  };
}

function toLevel(level: PayloadLevel): NestedTableLevel {
  const editors = (level.inputColumns ?? []).filter((column) => !!column?.field);
  // A column queried only to prefill an input / dropdown shows as that editor.
  const editorFields = new Set(editors.map((column) => column.field));
  const columns = (level.columns ?? []).filter((column) => !!column?.field && !editorFields.has(column.field!));
  const all: NestedTableColumn[] = [
    ...columns.map((column): NestedTableColumn => ({ field: column.field!, label: column.label || column.field!, ...sharedKeys(column) })),
    ...editors.map((column): NestedTableColumn => ({
      field: column.field!,
      label: column.label || column.field!,
      type: column.type === 'dropdown' ? 'dropdown' : 'input',
      ...(column.type === 'dropdown' ? { options: column.options ?? [] } : {}),
      ...sharedKeys(column),
    })),
  ];
  return {
    ...(level.title ? { title: level.title } : {}),
    ...(level.childrenField ? { childrenField: level.childrenField } : {}),
    // No columns = the table infers them from the rows, as the backend lists them.
    ...(all.length ? { columns: all } : {}),
    ...(Array.isArray(level.columnOrder) && level.columnOrder.length ? { columnOrder: level.columnOrder } : {}),
    ...(Array.isArray(level.actions) && level.actions.length ? { actions: level.actions.filter((action) => !!action?.label) } : {}),
    ...(level.rowSave?.url ? { rowSave: level.rowSave } : {}),
  };
}

/** Expander, mandatory flag, generic cell configuration and pairing, as the renderer reads them. */
function sharedKeys(column: PayloadColumnBase): Partial<NestedTableColumn> {
  // Generic cell configuration (shared editor) rides the payload column.
  const cellConfig = normalizeTableCellConfig(column.cellConfig);
  const secondField = typeof column.secondField === 'string' ? column.secondField.trim() : '';
  return {
    ...(column.expander ? { expander: true } : {}),
    ...(column.required ? { required: true } : {}),
    ...(cellConfig ? { cellConfig } : {}),
    ...(secondField && secondField !== column.field ? { secondField } : {}),
    ...(secondField && typeof column.separator === 'string' && column.separator.trim() ? { separator: column.separator.trim() } : {}),
    ...(column.hidden === true ? { hidden: true } : {}),
    ...(typeof column.decimals === 'number' ? { decimals: column.decimals } : {}),
    ...(column.scale === 'auto' || column.scale === 'K' || column.scale === 'M' || column.scale === 'B' ? { scale: column.scale } : {}),
  };
}

/** The table's own content keys — a Format value never replaces these. */
const NESTED_DATA_KEYS: ReadonlySet<string> = new Set(['title', 'subtitle', 'levels', 'staticData', 'rows', 'apiUrl', 'levelApiUrl', 'levelQueries', 'theme']);

/** The tile Format's look, as the table's flat theme keys. */
function themeFromFormat(format: Record<string, unknown>): Partial<NestedTableData> {
  const theme: Record<string, unknown> = {};
  if (typeof format['fontFamily'] === 'string' && format['fontFamily']) theme['fontFamily'] = format['fontFamily'];
  if (typeof format['fontSize'] === 'number' && format['fontSize'] > 0) theme['fontSize'] = format['fontSize'];
  NESTED_THEME_KEYS.forEach((key) => {
    const value = format[key];
    if (value === undefined || value === null || value === '') return;
    if (key === 'fontSize' && !(Number(value) > 0)) return; // 0 = "default", not 0px
    theme[key] = value;
  });
  // Every other Format setting rides flat too (row / column lines, header
  // height, text sizes, Level n …) — the table reads them off its data.
  Object.entries(format).forEach(([key, value]) => {
    if ((NESTED_THEME_KEYS as ReadonlyArray<string>).includes(key) || NESTED_DATA_KEYS.has(key)) return;
    if (value === undefined || value === null || value === '') return;
    theme[key] = value;
  });
  return theme as Partial<NestedTableData>;
}
